import React, { SyntheticEvent, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ONE_PAGE_SECTIONS } from 'constants/businessToolSections/onePage';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import {
  EXECUTIVE_SUMMARY_ORGANIZATION_AND_TEAM_NAMES,
  prefix as executiveSummaryPrefix,
} from 'components/BusinessToolForm/Sections/ExecutiveSummary/TeamExecSummary/utils/form';
import { PersonnelProfiles } from 'components/BusinessToolForm/Sections/ManagementAndOrganization/ManagementTeam/PersonnelProfiles';
import {
  MANAGEMENT_TEAM_NAMES,
  prefix as managementTeamPrefix,
  personnelProfilesValue,
} from 'components/BusinessToolForm/Sections/ManagementAndOrganization/ManagementTeam/utils/form';

import { concatWithDot } from 'helpers/string';

export const Team: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const [teamMobileExpanded, setTeamMobileExpanded] = useState<string | false>(false);

  const canAccess = useCheckSubSectionAccess(ONE_PAGE_SECTIONS.TEAM);

  const handleTeamAccordionChange = useCallback(
    (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => {
      setTeamMobileExpanded(isExpanded ? panel : false);
    },
    [],
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader locked={!canAccess} title={t('chapters.TEAM')} section={ONE_PAGE_SECTIONS.TEAM} />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('executiveSummary.team.teamMembers')}
        aiFieldName={concatWithDot(executiveSummaryPrefix, EXECUTIVE_SUMMARY_ORGANIZATION_AND_TEAM_NAMES.MEMBERS)}
        aiOnlyUpdate
      >
        {/* 1.5.1 */}
        <TextInputControl
          multiline
          control={control}
          id="onePage.team.teamMembers"
          name={concatWithDot(executiveSummaryPrefix, EXECUTIVE_SUMMARY_ORGANIZATION_AND_TEAM_NAMES.MEMBERS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1000}
        />
      </FormSectionCard>
      <FormSectionCard disabled={!canAccess} title={t('management.team.keyPersonnel')} collapsible>
        <FieldArrayComponent
          name={concatWithDot(managementTeamPrefix, MANAGEMENT_TEAM_NAMES.PERSONNEL_PROFILES)}
          initialItem={personnelProfilesValue}
          buttonText={t('management.team.keyPersonnelButtonText')}
          disabled={!canAccess}
        >
          {(namePrefix, index, remove) => (
            <PersonnelProfiles
              name={namePrefix}
              index={index}
              disabled={!canAccess}
              onRemove={remove}
              accordionExpanded={teamMobileExpanded}
              onAccordionChange={handleTeamAccordionChange}
            />
          )}
        </FieldArrayComponent>
      </FormSectionCard>
    </>
  );
};
